import { batchActions } from 'redux-batched-actions';
import { clamp, get, omit, uniq, without } from 'lodash';
import { SURVEY_SECTION_LANDING } from 'sharedParts';
import { replaceRoute } from 'actions/appActions';
import numbering from 'sharedParts/utils/numbering';
import getLocalStorageObject from 'utils/getLocalStorageObject';
import setLocalStorageObject from 'utils/setLocalStorageObject';
import mapEditorSectionToUrl from 'config/mapEditorSectionToUrl';
import surveyEndRedirectPage from 'constants/surveyEndRedirectPage';

import {
  clearInvalidFormNameToScroll,
  popFromVisitedPages,
  pushToVisitedPages,
  pushToVisitedQuestions,
  resetSurveyData,
  setAnswer,
  setCurrentPage,
  setInvalidFormNameToScroll,
  setLogicRedirectToQuestionId,
  setPendingLogicRedirectToQuestionId,
  setSectionHeaders,
  setSharedHeader,
  setSurveyBlockList,
  setSurveyInitialSettings,
  setVisitedPages,
  setQuestionNumbers,
  setVisitedQuestionNumbers,
  setVisitedQuestions,
  submitInvalidQuestionForms,
  updateSurveySettings,
} from 'taker/actions/appActions';

import {
  getCurrentRouterRootPath,
  getEditorEntityId,
  getEndPageNumber,
  getSurveyPreviewCurrentBlockList,
  getSurveyPreviewCurrentSectionName,
  getSurveyPreviewFirstInvalidFormName,
  getSurveyPreviewLatestVisitedPageNumber,
  getSurveyPreviewNextPage,
  getSurveyPreviewPendingRedirectIdByNextPageNumber,
  getSurveyPreviewPendingRedirectToBlockId,
  getSurveyPreviewQuestionLogicRedirectBlockId,
  getSurveyPreviewRedirectUrl,
  getSurveyPreviewVisitedPages,
  getSurveyPreviewVisitedQuestions,
  hasSurveyPreviewEditorHeader,
  isAllSurveyPreviewPageAnswersValid,
  isSurveyLandingEnabled,
  isSurveyPreviewBlockNextOrSubmitButton,
  isSurveyPreviewEndEnabled,
  isSurveyPreviewLastPage,
  isSurveyPreviewQuestionLogicValid,
} from 'reducers';

import isPageLoadedInIframe from '../utils/surveyEmbedding/isPageLoadedInIframe';
import { getQuestionBlockIds } from '../utils/getQuestionBlockIds';

const getSurveyEditorPersistedState = surveyId => {
  return get(getLocalStorageObject('surveyEditor'), `${surveyId}`, {});
};

const setSurveyEditorPersistedState = (surveyId, data) => {
  setLocalStorageObject('surveyEditor', { [surveyId]: data });
};

export const resetSurveyEditorPersistedState = surveyId => {
  setSurveyEditorPersistedState(surveyId, {});
};

export const restoreRedirectToBlockId = () => (dispatch, getState) => {
  const surveyId = getEditorEntityId(getState());
  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);

  if (surveyEditorPersistedState.redirectToBlockId) {
    dispatch(
      batchActions([
        setLogicRedirectToQuestionId(
          surveyEditorPersistedState.redirectToBlockId,
        ),
        setPendingLogicRedirectToQuestionId(null),
      ]),
    );
  }
};

export {
  clearInvalidFormNameToScroll,
  popFromVisitedPages,
  pushToVisitedPages,
  resetSurveyData,
  setCurrentPage,
  setSharedHeader,
  setSectionHeaders,
  setSurveyBlockList,
  setSurveyInitialSettings,
  setQuestionNumbers,
  setVisitedQuestions,
  setVisitedQuestionNumbers,
  updateSurveySettings,
};

export const putToVisitedPages = value => (dispatch, getState) => {
  const latestVisitedPageNumber = getSurveyPreviewLatestVisitedPageNumber(
    getState(),
  );
  if (value !== latestVisitedPageNumber) {
    dispatch(pushToVisitedPages(value));
  }
};

const persistVisitedQuestions = (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);

  const visitedQuestions = getSurveyPreviewVisitedQuestions(state);
  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);

  surveyEditorPersistedState.visitedQuestions = visitedQuestions;
  setSurveyEditorPersistedState(surveyId, surveyEditorPersistedState);
};

export const putToVisitedQuestions = (dispatch, getState) => {
  const state = getState();
  const visitedQuestions = getSurveyPreviewVisitedQuestions(state);

  const currentPageQuestionIds = getQuestionBlockIds(
    getSurveyPreviewCurrentBlockList(state),
  );

  dispatch(
    pushToVisitedQuestions(
      uniq([...visitedQuestions, ...currentPageQuestionIds]),
    ),
  );

  dispatch(persistVisitedQuestions);
};

export const popFromVisitedQuestions = (dispatch, getState) => {
  const state = getState();
  const visitedQuestions = getSurveyPreviewVisitedQuestions(state);
  const lastPageBlockIds = getQuestionBlockIds(
    getSurveyPreviewCurrentBlockList(state),
  );

  dispatch(
    pushToVisitedQuestions(without(visitedQuestions, ...lastPageBlockIds)),
  );

  dispatch(persistVisitedQuestions);
};

export const redirectToPage = pageNumber => (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const root = getCurrentRouterRootPath(state);
  const hasEditorHeader = hasSurveyPreviewEditorHeader(state);
  const hasEditorHeaderParam =
    hasEditorHeader === false ? '?has_editor_header=false' : '';

  dispatch(setCurrentPage(pageNumber));
  dispatch(
    replaceRoute(
      `${root}/${surveyId}/${mapEditorSectionToUrl.preview}/${numbering.toHuman(
        pageNumber,
      )}${hasEditorHeaderParam}`,
    ),
  );
};

export const restorePreview = () => (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);

  const visitedPages = get(surveyEditorPersistedState, 'visitedPages', []);

  const visitedQuestions = get(
    surveyEditorPersistedState,
    'visitedQuestions',
    [],
  );

  dispatch(restoreRedirectToBlockId());

  dispatch(
    batchActions([
      setVisitedPages(visitedPages),
      setVisitedQuestions(visitedQuestions),
    ]),
  );
};

const persistVisitedPages = (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const visitedPages = getSurveyPreviewVisitedPages(state);

  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);
  surveyEditorPersistedState.visitedPages = visitedPages;

  setSurveyEditorPersistedState(surveyId, surveyEditorPersistedState);
};

const persistRedirectBlockId = blockId => (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);

  if (blockId) {
    surveyEditorPersistedState.redirectToBlockId = blockId;
    setSurveyEditorPersistedState(surveyId, surveyEditorPersistedState);
  } else {
    setSurveyEditorPersistedState(
      surveyId,
      omit(surveyEditorPersistedState, 'redirectToBlockId'),
    );
  }
};

export const resetPreview = () => dispatch => {
  dispatch(setVisitedPages([]));
  dispatch(setVisitedQuestions([]));
  dispatch(setLogicRedirectToQuestionId(null));
  dispatch(persistVisitedPages);
  dispatch(persistVisitedQuestions);
  dispatch(persistRedirectBlockId(null));
};

export const setCurrentPreviewPage = pageNumber => (dispatch, getState) => {
  const state = getState();
  const validPage = Number.isInteger(pageNumber)
    ? clamp(pageNumber, 0, getEndPageNumber(state))
    : 0;
  const isLandingDisabled = validPage === 0 && !isSurveyLandingEnabled(state);
  if (!isLandingDisabled) {
    dispatch(putToVisitedPages(validPage));
    dispatch(persistVisitedPages);
  }
  dispatch(setCurrentPage(validPage));
};

export const goToRedirectUrl = () => (dispatch, getState) => {
  const redirectUrl = getSurveyPreviewRedirectUrl(getState());
  if (!redirectUrl) {
    return;
  }

  if (isPageLoadedInIframe()) {
    window.open(redirectUrl, '_blank');
    return;
  }

  window.location.assign(redirectUrl);
};

export const handleLogicRedirectOnNextPage = nextPage => (
  dispatch,
  getState,
) => {
  const state = getState();
  const pendingRedirectId = getSurveyPreviewPendingRedirectIdByNextPageNumber(
    state,
    nextPage,
  );

  if (pendingRedirectId) {
    dispatch(setPendingLogicRedirectToQuestionId(pendingRedirectId));
  }
};

export const goToNextPage = () => (dispatch, getState) => {
  const state = getState();
  const nextPage = getSurveyPreviewNextPage(state);
  const isLastPage = isSurveyPreviewLastPage(state);

  if (nextPage === surveyEndRedirectPage) {
    dispatch(goToRedirectUrl());
    return;
  }

  if (isLastPage) {
    const isEndEnabled = isSurveyPreviewEndEnabled(state);
    const isLandingSection =
      getSurveyPreviewCurrentSectionName(state) === SURVEY_SECTION_LANDING;
    if (!isEndEnabled && !isLandingSection) {
      dispatch(goToRedirectUrl());
    }

    return;
  }

  dispatch(handleLogicRedirectOnNextPage(nextPage));
  dispatch(redirectToPage(nextPage));
};

const resetPersistedRedirectBlockId = (
  surveyEditorPersistedState,
  surveyId,
) => dispatch => {
  setSurveyEditorPersistedState(
    surveyId,
    omit(surveyEditorPersistedState, 'redirectToBlockId'),
  );

  dispatch(setLogicRedirectToQuestionId(null));
};

/* removes stored redirectBlockId if user navigated back to a page that has redirect */
const verifyRedirectBlockId = (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const surveyEditorPersistedState = getSurveyEditorPersistedState(surveyId);

  const currentBlockList = getSurveyPreviewCurrentBlockList(getState());
  const block = currentBlockList.find(
    block => block.id === surveyEditorPersistedState.redirectToBlockId,
  );

  if (block) {
    dispatch(
      resetPersistedRedirectBlockId(surveyEditorPersistedState, surveyId),
    );
  }
};

export const goToPreviousPage = (dispatch, getState) => {
  dispatch(popFromVisitedPages());
  dispatch(persistVisitedPages);
  dispatch(popFromVisitedQuestions);
  dispatch(verifyRedirectBlockId);

  const latestVisitedPage = getSurveyPreviewLatestVisitedPageNumber(getState());
  dispatch(redirectToPage(latestVisitedPage));
};

export const handleLogicRedirectOnSubmit = () => (dispatch, getState) => {
  const state = getState();
  const pendingLogicRedirectToQuestionId = getSurveyPreviewPendingRedirectToBlockId(
    state,
  );

  dispatch(
    batchActions([
      setLogicRedirectToQuestionId(pendingLogicRedirectToQuestionId),
      setPendingLogicRedirectToQuestionId(null),
    ]),
  );

  dispatch(persistRedirectBlockId(pendingLogicRedirectToQuestionId));
};

export const handleNextSubmitPress = () => (dispatch, getState) => {
  const state = getState();
  if (isAllSurveyPreviewPageAnswersValid(state)) {
    dispatch(handleLogicRedirectOnSubmit());
    dispatch(goToNextPage());
  } else {
    dispatch(submitInvalidQuestionForms());
    dispatch(
      setInvalidFormNameToScroll(getSurveyPreviewFirstInvalidFormName(state)),
    );
  }
};

const handleLogicRedirectOnAnswer = (questionId, answer) => (
  dispatch,
  getState,
) => {
  const state = getState();
  if (isSurveyPreviewQuestionLogicValid(state, questionId)) {
    const blockToJumpId = getSurveyPreviewQuestionLogicRedirectBlockId(
      state,
      questionId,
      answer,
    );
    dispatch(setPendingLogicRedirectToQuestionId(blockToJumpId));
    dispatch(persistRedirectBlockId(blockToJumpId));
  }
};

export const answerQuestion = (id, answer) => (dispatch, getState) => {
  const state = getState();

  if (isSurveyPreviewBlockNextOrSubmitButton(state, id)) {
    dispatch(handleNextSubmitPress());
    return;
  }

  dispatch(setAnswer(id, answer));
  dispatch(handleLogicRedirectOnAnswer(id, answer));
};
